import type { CharacterDef } from '../characters/types';
import { LUMINOSITY_STEPS } from '../board/types';
import type { GameContext, GameState } from './types';

/**
 * The elementals a mage can summon, and which one the luminosity cycle brings. The cycle runs
 * from black to white; the four elements are ordered as the ancients stacked them, from the
 * heaviest to the lightest: earth, water, air, fire. So the darker the board, the heavier the
 * element. The two peaks of the cycle are visited half as often as the phases between them, so
 * the elements at the ends take two phases each: over a whole game every elemental is about
 * equally likely.
 */
export type Element = 'earth' | 'water' | 'air' | 'fire';
export const ELEMENTS: readonly Element[] = ['earth', 'water', 'air', 'fire'];

/** Phase 0 (black) … 5 (white) → element; see the module comment. */
const ELEMENT_OF_PHASE: readonly Element[] = ['earth', 'earth', 'water', 'air', 'fire', 'fire'];

export function elementOfPhase(phase: number): Element {
  const clamped = Math.max(0, Math.min(LUMINOSITY_STEPS - 1, Math.floor(phase)));
  return ELEMENT_OF_PHASE[clamped]!;
}

/** Roster id of an element's elemental: `elemental-fire` and so on. */
export function elementalId(element: Element): string {
  return `elemental-${element}`;
}

/** The element of an elemental's roster id, or `undefined` for any other character. */
export function elementOf(character: string): Element | undefined {
  return ELEMENTS.find((e) => elementalId(e) === character);
}

/**
 * The elemental Summon Elemental brings right now: the roster's creature of the current phase's
 * element, provided the roster marks it `summoned`. A roster without elementals simply has no
 * summons to offer.
 */
export function summonedElemental(ctx: GameContext, state: GameState): CharacterDef | undefined {
  const character = ctx.roster[elementalId(elementOfPhase(state.luminosity.phase))];
  return character?.summoned ? character : undefined;
}
